import React, { useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CloseOutline } from '@lyearn/icons';
import { IconButton } from '@lyearn/ui';
import FileInput from '@/components/FileInput';
import { JobEntityStatusEnum } from '@/types/schema';

import JobStatusItem from '../../components/JobStatusItem';
import SmallJobStatusItem from './components/SmallJobStatusItem';
import UpdateDataDialog from './components/UpdateDataDialog';
import useImportJob from './hooks/useImportJob';
import { importItemConfig } from './config';
import { BulkImportProps } from './types';

const BulkImport: React.FC<BulkImportProps> = (props) => {
  const {
    className,
    uppy,
    fileInputRef,
    currentImportJob,
    onImportStatusUpdate,
    updateDataDialogProps,
    failedSnackbarMessage,
    jobPurpose,
    size,
    refetchJobs,
    onComplete,
    onClose,
    preventRefetch,
    fileType = 'csv',
    hideShowDetailsButton = false,
    referenceId,
  } = props;

  const { t } = useTranslation();

  const [showUpdateDataDialog, { on, off }] = useToggle(false);
  const [isButtonHovered, hoverActions] = useToggle(false);

  const { job, onDownloadReport, onCancel } = useImportJob({
    purpose: jobPurpose,
    onImportStatusUpdate,
    refetchJobs,
    onShowUpdateDialogDetails: on,
    failedSnackbarMessage,
    importJobScheduleId: currentImportJob?.id,
    onComplete,
    preventRefetch,
    hideShowDetailsButton,
  });

  // TODO: Use same props + config for both JobStatusItem
  const renderItem = () => {
    if (!currentImportJob) {
      return null;
    }

    const jobStatus = job?.status ?? JobEntityStatusEnum.NotStarted;

    if (size === 'small') {
      return (
        <SmallJobStatusItem
          className={className}
          jobStatus={jobStatus}
          title={currentImportJob!.name}
          onCancel={onCancel}
          onDownloadReport={onDownloadReport}
        />
      );
    }

    const config = importItemConfig(t, onDownloadReport, onCancel)[jobStatus];

    return (
      <JobStatusItem
        className={className}
        description={t(config.descriptionKey)}
        leftNode={config.Icon}
        progress={config.progress}
        rightNode={
          onClose &&
          isButtonHovered &&
          (jobStatus === JobEntityStatusEnum.Completed || !config.rightNode) ? (
            <IconButton
              Icon={CloseOutline}
              appearance="secondary"
              shape="square"
              size="s"
              onClick={onClose}
            />
          ) : (
            config.rightNode
          )
        }
        title={currentImportJob!.name}
        onMouseOut={hoverActions.off}
        onMouseOver={hoverActions.on}
      />
    );
  };

  const fileMeta = useMemo(() => ({ referenceId: referenceId ?? jobPurpose[0] }), [jobPurpose]);

  return (
    <>
      <FileInput ref={fileInputRef} accept={`.${fileType}`} fileMeta={fileMeta} uppy={uppy} />
      {renderItem()}
      {showUpdateDataDialog ? (
        <UpdateDataDialog
          getDescription={updateDataDialogProps.getDescription}
          getUpdateItems={updateDataDialogProps.getUpdateItems}
          updateData={job?.meta}
          onClose={off}
        />
      ) : null}
    </>
  );
};

export default BulkImport;
